'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, forwardRef, useEffect, useRef, } from 'react';
import { useCopilot } from './context.js';
import { collectAllTerminalStates, CopilotToolCall, groupParts, } from './tool-call.js';
// COP-135 D1/D6 — the server-sent action rows. The client renders them and
// derives nothing (see that module's docblock).
import { CopilotTurnActions, hasTurnActionRows, indexTurnActions, routeTurnActions, } from './turn-actions.js';
import { MemorySuggestionCard, } from './memory-suggestion.js';
import { DocShareSuggestionCard, } from './doc-share-suggestion.js';
import { ForgetMemoryCard, } from './forget-memory-card.js';
import { ApprovalCard, } from './approval-card.js';
// The single approval wire-shape → card-shape mapping, shared with the
// batteries-included widget so the two render paths cannot drift (ATRU-01).
import { approvalPartToCardData } from './approval-card-data.js';
import { JobCard } from './job-card.js';
import { PlanCard, } from './plan-card.js';
// COP-092 retire plan-mode: PlanActivationNotice import removed.
import { SchedulePreviewCard, } from './schedule-preview-card.js';
import { DbTableCard } from './db-table-card.js';
/**
 * Renders the current message list. Without children it emits an unstyled
 * structure with class hooks (`.copilot-message`, `.copilot-message-user`,
 * `.copilot-message-assistant`, `.copilot-message-text`). Provide a render
 * prop child to take full control.
 */
export const CopilotMessages = forwardRef(function CopilotMessages({ children, renderToolCall, renderMemorySuggestion, renderDocShareSuggestion, renderForgetMemory, renderApprovalCard, renderPlanCard, renderSchedulePreviewCard, renderSkillLoaded, renderDbTable, autoScroll = true, className, ...rest }, forwardedRef) {
    const { messages } = useCopilot();
    const endRef = useRef(null);
    useEffect(() => {
        if (!autoScroll)
            return;
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, autoScroll]);
    return (_jsxs("div", { ref: forwardedRef, className: ['copilot-messages', className].filter(Boolean).join(' '), ...rest, children: [(() => {
                // Cross-message pre-pass — ONE walk over the full message list
                // collects terminal SSE part states for all five card families
                // (memory-suggestion, doc-share, approval, schedule-preview, plan)
                // keyed by their id, so a pending card on an EARLIER assistant
                // message renders directly in its terminal state when the user's
                // decide/confirm produced the terminal part on a LATER assistant
                // message. Single-homed in `collectAllTerminalStates` (tool-call.tsx);
                // destructured below into the same five local names the grouper opts
                // consume. (UAT 2026-05-16 follow-up #1; D-02; D-22/SCUX-07; reload
                // plan-card seeding.)
                const { memory: terminalStates, docShare: docShareTerminalStates, forgetMemory: forgetMemoryTerminalStates, approval: approvalTerminalStates, schedulePreview: schedulePreviewTerminalStates, plan: planTerminalStates, planExecution: planExecutionStates, } = collectAllTerminalStates(messages);
                // COP-135 D1 (task-07 AC15) — the SECOND cross-message pre-pass, and
                // for the same reason as the first: a settle emits the list of the
                // turn that MINTED the card, on whichever request the user decided
                // through. Routing on the server's `turnId` puts each list back on the
                // message the server addressed. One walk, single-homed in
                // `routeTurnActions`.
                const turnActionsByMessage = routeTurnActions(messages);
                // COP-135 D1 (task-08) — the same routed lists, re-keyed by
                // `actionId` for the cards that show their OWN outcome. Built here,
                // once, from the routed result rather than from the raw parts, so the
                // rows a card joins to are exactly the rows the transcript renders.
                const turnActionIndex = indexTurnActions(turnActionsByMessage);
                return messages.map((msg, index) => {
                    const rawParts = (msg.parts ?? []);
                    const groups = groupParts(rawParts, {
                        role: msg.role,
                        terminalStates,
                        docShareTerminalStates,
                        forgetMemoryTerminalStates,
                        approvalTerminalStates,
                        schedulePreviewTerminalStates,
                        planTerminalStates,
                        planExecutionStates,
                    });
                    // Suppress the whole message bubble when the grouper produced nothing
                    // (e.g. a user message whose sole text part was a memory envelope —
                    // the card carries the visible feedback; the raw JSON must not appear
                    // as a chat bubble).
                    //
                    // COP-135 D1 — …UNLESS the turn carries action rows. The grouper
                    // suppresses five real-action tool families (each owns a card), so a
                    // turn whose only visible record is its action list would otherwise be
                    // dropped entirely — the copilot silently discarding actions it
                    // actually took, which is the defect this milestone closes. Read
                    // through the ONE reader; nothing is derived here.
                    const turnActions = turnActionsByMessage[index] ?? [];
                    if (groups.length === 0 && !hasTurnActionRows(turnActions))
                        return null;
                    const isLast = index === messages.length - 1;
                    if (children) {
                        return (_jsx("div", { "data-copilot-message-wrapper": "", children: children({
                                message: msg,
                                index,
                                isLast,
                                groups,
                                turnActions,
                                turnActionIndex,
                            }) }, msg.id));
                    }
                    return (_jsx("div", { className: `copilot-message copilot-message-${msg.role}`, "data-role": msg.role, children: _jsxs("div", { className: "copilot-message-body", children: [groups.map((group, gi) => {
                                    if (group.type === 'text') {
                                        return (_jsx("div", { className: "copilot-message-text", children: group.text }, gi));
                                    }
                                    if (group.type === 'memory-suggestion') {
                                        return (_jsx(MemorySuggestionCard, { suggestion: group.suggestion, children: renderMemorySuggestion }, gi));
                                    }
                                    if (group.type === 'doc-share-suggestion') {
                                        return (_jsx(DocShareSuggestionCard, { suggestion: group.suggestion, children: renderDocShareSuggestion }, gi));
                                    }
                                    if (group.type === 'forget-memory') {
                                        return (_jsx(ForgetMemoryCard, { suggestion: group.suggestion, children: renderForgetMemory }, gi));
                                    }
                                    if (group.type === 'approval') {
                                        // Phase 5 (D-02): map the grouper's `ApprovalPart` into
                                        // the `ApprovalCardData` shape the card consumes. The
                                        // two are wire-compatible; the card adds an internal
                                        // local state on top.
                                        const data = approvalPartToCardData(group.approval);
                                        return (_jsx(ApprovalCard, { approval: data, children: renderApprovalCard }, gi));
                                    }
                                    if (group.type === 'job') {
                                        // COP-156 D5 (TFDX-13g): a parked long-running track job.
                                        return _jsx(JobCard, { job: group.job }, gi);
                                    }
                                    if (group.type === 'skill-loaded') {
                                        if (renderSkillLoaded) {
                                            return (_jsx(Fragment, { children: renderSkillLoaded({ skill: group.skill }) }, gi));
                                        }
                                        return (_jsxs("div", { className: "copilot-skill-loaded", "data-skill-id": group.skill.skillId, children: ["Loaded skill:", ' ', _jsx("span", { className: "copilot-skill-loaded-name", children: group.skill.name })] }, gi));
                                    }
                                    if (group.type === 'db-table') {
                                        if (renderDbTable) {
                                            return _jsx(Fragment, { children: renderDbTable(group.data) }, gi);
                                        }
                                        return _jsx(DbTableCard, { data: group.data }, gi);
                                    }
                                    // COP-092 retire plan-mode: removed the 'plan-activation-notice' group render.
                                    if (group.type === 'plan') {
                                        return (_jsx(PlanCard, { plan: planPartToCardData(group.plan), initialState: group.initialState, execution: group.execution, actions: turnActionIndex, children: renderPlanCard }, gi));
                                    }
                                    if (group.type === 'schedule-preview') {
                                        // Plan 02-06 (D-22): map the grouper's terminalState
                                        // (pending → approved/rejected/expired/error) into the
                                        // card's `initialTerminalState` prop. The card flips
                                        // automatically when the cross-message collector
                                        // populates this on a subsequent render.
                                        return (_jsx(SchedulePreviewCard, { preview: group.preview, initialTerminalState: group.terminalState, resolvedAt: group.resolvedAt, actions: turnActionIndex, children: renderSchedulePreviewCard }, gi));
                                    }
                                    return (_jsx(CopilotToolCall, { tools: group.tools, children: renderToolCall }, gi));
                                }), _jsx(CopilotTurnActions, { snapshots: turnActions })] }) }, msg.id));
                });
            })(), _jsx("div", { ref: endRef, "aria-hidden": "true" })] }));
});
// Exported for reuse by host compositions that override the default
// CopilotMessages render path (e.g. CopilotPanelContent, CopilotWidget) —
// otherwise those would have to re-implement the PlanPart → PlanCardProps
// mapping. Plan 02 WR-05.
export function planPartToCardData(plan) {
    return {
        type: 'tf-plan-pending',
        plan: {
            id: plan.id,
            sessionId: plan.sessionId,
            trackId: plan.trackId,
            status: plan.status,
            payloadHash: plan.payloadHash,
            expiresAt: plan.expiresAt,
            createdAt: plan.createdAt,
            steps: plan.steps.map(({ references, ...step }) => ({
                ...step,
                ...(references ? { references: references.map((ref) => ({ ...ref })) } : {}),
            })),
            options: plan.options,
        },
        actions: plan.actions,
        copy: plan.copy,
    };
}
//# sourceMappingURL=messages.js.map